// client/src/pages/admin/AdminBannerList.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, IconButton, Stack, Alert, CircularProgress, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ViewCarouselOutlinedIcon from '@mui/icons-material/ViewCarouselOutlined';
import EmptyState from '../../components/EmptyState';
import { API_BASE_URL, getImageUrl } from '../../utils/media';

const AdminBannerList = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Only recreated when the picked File actually changes - re-running this
  // on every keystroke in the link field would leak a fresh blob URL each time.
  const filePreviewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => { if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl); }, [filePreviewUrl]);

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchBanners = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/banners`, { headers: authHeader() });
      if (!response.ok) throw new Error('Failed to load banners.');
      setBanners(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBanners(); }, [fetchBanners]);

  const openForm = () => {
    setFile(null);
    setLinkUrl('');
    setFormError('');
    setFormOpen(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!file) { setFormError('Please choose an image.'); return; }
    setFormError('');
    setSubmitting(true);
    try {
      const body = new FormData();
      body.append('image', file);
      if (linkUrl) body.append('linkUrl', linkUrl);
      const response = await fetch(`${API_BASE_URL}/api/admin/banners`, {
        method: 'POST', headers: authHeader(), body,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to add banner.');
      setFormOpen(false);
      fetchBanners();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this banner slide?')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/banners/${id}`, { method: 'DELETE', headers: authHeader() });
      if (!response.ok) throw new Error('Failed to delete banner.');
      fetchBanners();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleMove = async (id, direction) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/banners/${id}/move`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ direction }),
      });
      if (!response.ok) throw new Error('Failed to reorder banner.');
      fetchBanners();
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Typography variant="h6" fontWeight={700}>Rotating Banner Slides</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openForm}>Add Banner</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Your main banner (set above) always shows first, followed by these slides in order. They save as soon as you add, move, or remove them.
        With 2 or more slides, the homepage banner rotates automatically.
      </Typography>

      {banners.length === 0 ? (
        <EmptyState
          icon={<ViewCarouselOutlinedIcon />}
          title="No extra banner slides yet"
          description="Add slides for sales, promotions, or announcements - they'll rotate alongside your main banner."
        />
      ) : (
        <Stack spacing={1.5}>
          {banners.map((banner, index) => (
            <Box
              key={banner.id}
              sx={{
                display: 'flex', alignItems: 'center', gap: 2, p: 1.5,
                border: 1, borderColor: 'divider', borderRadius: 1,
              }}
            >
              <Avatar src={getImageUrl(banner.image_url)} variant="rounded" sx={{ width: 80, height: 48 }} />
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap>
                  {banner.link_url || <em>No link</em>}
                </Typography>
              </Box>
              <IconButton size="small" disabled={index === 0} onClick={() => handleMove(banner.id, 'up')} aria-label="Move up">
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" disabled={index === banners.length - 1} onClick={() => handleMove(banner.id, 'down')} aria-label="Move down">
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => handleDelete(banner.id)} aria-label="Delete banner">
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} fullWidth maxWidth="xs">
        <Box component="form" onSubmit={handleCreate}>
          <DialogTitle>Add Banner Slide</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {formError && <Alert severity="error">{formError}</Alert>}
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5 }}>Image</Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  {filePreviewUrl && (
                    <Avatar src={filePreviewUrl} variant="rounded" sx={{ width: 80, height: 48 }} />
                  )}
                  <Button component="label" variant="outlined" size="small" startIcon={<UploadFileIcon />}>
                    {file ? 'Replace' : 'Upload'}
                    <input hidden type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0] || null)} />
                  </Button>
                </Stack>
              </Box>
              <TextField
                label="Link URL (optional)" fullWidth
                helperText="Where this slide takes a visitor when clicked - e.g. /?category=RC Planes"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={submitting}>Add</Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default AdminBannerList;
